import type { TypedDocumentNode } from '@apollo/client'
import { gql } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, stripSearchParams, useRouterState } from '@tanstack/react-router'
import { SFProfileBanner } from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { EllipsisIcon, UserXIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import type {
  BlocksPageQuery,
  BlocksPageQueryVariables,
  UnblockProfileMutation,
  UnblockProfileMutationVariables,
} from 'types/graphql'
import { z } from 'zod'

const QUERY: TypedDocumentNode<BlocksPageQuery, BlocksPageQueryVariables> = gql`
  query BlocksPage($page: Int, $filter: BlockPageFilter, $sortBy: BlockSortEnum) {
    blocks: blocksPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        id
        blocksMe {
          ...ProfileInfo
        }
      }
      count
      perPage
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

export const UNBLOCK_PROFILE: TypedDocumentNode<UnblockProfileMutation, UnblockProfileMutationVariables> = gql`
  mutation UnblockProfile($id: String!) {
    unblockProfileById(id: $id)
  }
`

type BlockedListSort = 'OLDEST' | 'LATEST' | 'NAME'

type BlockedSearchInput = {
  name?: string
  page?: number
  sortBy?: BlockedListSort
}

function resolveBlockedSearchParams(search: BlockedSearchInput): {
  page: number
  name?: string
  sortBy: BlockedListSort
} {
  return {
    page: search.page ?? 1,
    name: search.name,
    sortBy: search.sortBy ?? 'LATEST',
  }
}

export const Route = createFileRoute('/(auth)/(account-layout)/account/$profileId/blocked')({
  component: PageComponent,
  validateSearch: z.object({
    name: z.string().optional(),
    page: z.coerce.number().optional(),
    sortBy: z.enum(['OLDEST', 'LATEST', 'NAME']).optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        sortBy: 'LATEST',
      }),
    ],
  },
  loaderDeps: ({ search }) => resolveBlockedSearchParams(search),
  head: () =>
    seo({
      title: `Blocked Profiles | ${env.VITE_APP_NAME}`,
      description:
        'View and manage your blocked profiles list. Browse blocked profiles, search by name, and unblock profiles.',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context, params, deps }) => {
    const { queryRef } = await queryAndPreload(context, QUERY, {
      variables: {
        page: deps.page,
        sortBy: deps.sortBy,
        filter: {
          profileId: params.profileId,
          name: deps.name,
        },
      },
    })
    return { queryRef }
  },
})

function PageComponent() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const { data } = useReadQuery(queryRef as QueryRef<BlocksPageQuery>)
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const search = Route.useSearch()
  const resolvedSearch = useMemo(() => resolveBlockedSearchParams(search), [search])
  const { profileId } = Route.useParams()
  const [modalDialog, setModalDialog] = useState(false)
  const [unblockId, setUnblockId] = useState<string | null>(null)
  const navigate = Route.useNavigate()
  const { currentUser } = useAuth()

  const isLoading = useRouterState({ select: (s) => s.status === 'pending' })
  const [showLoading, setShowLoading] = useState(false)

  const formMethods = useForm({
    defaultValues: {
      name: search.name,
      sortBy: resolvedSearch.sortBy,
    },
  })

  useEffect(() => {
    if (!isLoading) {
      setShowLoading(false)
    }
  }, [isLoading])

  const [unblockProfile] = useMutation(UNBLOCK_PROFILE, {
    onCompleted: () => {
      toast.success(t('Profile Unblocked'))
      setShowSpinnerValue(false)
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
    update(cache, _, { variables }) {
      // Evict the block entry from cache
      cache.evict({ id: cache.identify({ __typename: 'Block', id: variables?.id }) })
      cache.gc()
    },
  })

  const handleDialogAccept = () => {
    setShowSpinnerValue(true)
    setModalDialog(false)
    unblockProfile({ variables: { id: unblockId } })
  }

  const handleFilter = ({ page, name, sortBy }: { page?: number; name?: string; sortBy?: string }): void => {
    setShowLoading(true)
    navigate({
      search: {
        page: page ?? resolvedSearch.page,
        sortBy: sortBy ?? resolvedSearch.sortBy,
        name: name ?? search.name,
      },
      replace: true,
    } as never)
  }

  const entryCount = data?.blocks?.entries?.length ?? 0

  return (
    <div className="mx-auto w-full max-w-[90rem] px-0">
      <div className="flex w-full flex-col gap-4">
        <h1 className="sr-only">{t('Blocked Profiles Listing')}</h1>

        <Form className="w-full" formMethods={formMethods} suppressHydrationWarning>
          <SearchExpand
            className="w-full"
            clearSearchLabel={t('Clear search')}
            fieldProps={{ className: 'basis-0 flex-1 min-w-0' }}
            loading={showLoading}
            name="name"
            onSearchChange={(value) => handleFilter({ name: value })}
            placeholder={t('Search blocked profiles...')}
            rightBarChildren={
              <div className="flex w-[min(100%,180px)] shrink-0 basis-[180px] items-start">
                <NativeSelectFormField
                  aria-label={t('Sort By')}
                  fieldProps={{ className: 'w-full max-w-full' }}
                  name="sortBy"
                  onValueChange={(e) => handleFilter({ sortBy: e.target.value })}
                  selectProps={{ className: 'w-full min-w-0' }}
                >
                  <option value="NAME">{t('Name')}</option>
                  <option value="LATEST">{t('Latest')}</option>
                  <option value="OLDEST">{t('Oldest')}</option>
                </NativeSelectFormField>
              </div>
            }
            toggleFiltersLabel={t('Toggle filters')}
          />
        </Form>

        {entryCount > 0 ? (
          <div className={cn('grid w-full grid-cols-1 gap-2')}>
            {data?.blocks?.entries.map((block) => {
              return (
                <Card className="p-2" key={block.id}>
                  <CardContent className="p-0">
                    <SFProfileBanner
                      lowerChild={`${block?.blocksMe?.location}`}
                      profile={block.blocksMe}
                      rightChild={
                        currentUser?.profile?.id === profileId && (
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              render={
                                <Button
                                  aria-label={t('Menu')}
                                  className="rounded-full"
                                  disabled={showSpinner}
                                  size="icon-sm"
                                  type="button"
                                  variant="default"
                                >
                                  {showSpinner ? <Spinner className="size-4" /> : <EllipsisIcon className="size-4" />}
                                </Button>
                              }
                            />
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  setUnblockId(block.id)
                                  setModalDialog(true)
                                }}
                              >
                                <UserXIcon className="mr-2 size-4" />
                                {t('Unblock')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )
                      }
                      shape="rounded"
                      size="2xl"
                    />
                  </CardContent>
                </Card>
              )
            })}
          </div>
        ) : (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <UserXIcon />
              </EmptyMedia>
              <EmptyTitle>
                {search.name
                  ? t('No blocked profiles found for "{{name}}"', { name: search.name })
                  : t('No blocked profiles at this time')}
              </EmptyTitle>
            </EmptyHeader>
          </Empty>
        )}

        <PaginationControl
          count={data?.blocks?.count || 0}
          onPageChange={(page) => handleFilter({ page: page.page })}
          page={resolvedSearch.page}
          pageSize={data?.blocks?.perPage}
        />
      </div>

      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Yes')}
        loading={showSpinner}
        onConfirm={handleDialogAccept}
        onOpenChange={(e) => setModalDialog(e.open)}
        open={modalDialog}
        text={t('Are you sure you want to unblock this profile?')}
        title={t('Are you sure?')}
        variant="basic"
      />
    </div>
  )
}
